'use client';

import { ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  Clock3,
  KeyRound,
  MoreHorizontal,
  ShieldOff,
  UserCog,
  UserX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AppUser } from '@/hooks/useUsuarios';
import {
  ADMIN_USER_STATUS_META,
  formatUserDate,
  getRoleLabel,
  getUserDisplayName,
  getUserInitials,
} from '@/lib/admin-users';

interface CreateColumnsOptions {
  onEdit?: (user: AppUser) => void;
  onChangeStatus?: (user: AppUser, nextStatus: AppUser['status']) => void;
  onResetPassword?: (user: AppUser) => void;
}

const UserActionsCell = ({
  user,
  onEdit,
  onChangeStatus,
  onResetPassword,
}: {
  user: AppUser;
  onEdit?: (user: AppUser) => void;
  onChangeStatus?: (user: AppUser, nextStatus: AppUser['status']) => void;
  onResetPassword?: (user: AppUser) => void;
}) => {
  const statusMeta = ADMIN_USER_STATUS_META[user.status];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Ações rápidas</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onEdit?.(user)}>
          <UserCog className="mr-2 h-4 w-4" />
          Editar usuário
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onResetPassword?.(user)}>
          <KeyRound className="mr-2 h-4 w-4" />
          Enviar redefinição de senha
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {user.status !== 'APPROVED' && (
          <DropdownMenuItem onClick={() => onChangeStatus?.(user, 'APPROVED')}>
            <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
            Aprovar usuário
          </DropdownMenuItem>
        )}
        {user.status !== 'PENDING' && (
          <DropdownMenuItem onClick={() => onChangeStatus?.(user, 'PENDING')}>
            <Clock3 className="mr-2 h-4 w-4 text-amber-600" />
            Marcar como pendente
          </DropdownMenuItem>
        )}
        {user.status !== 'REJECTED' && (
          <DropdownMenuItem onClick={() => onChangeStatus?.(user, 'REJECTED')}>
            <UserX className="mr-2 h-4 w-4 text-rose-600" />
            Rejeitar acesso
          </DropdownMenuItem>
        )}
        {user.status !== 'INACTIVE' ? (
          <DropdownMenuItem
            onClick={() => onChangeStatus?.(user, 'INACTIVE')}
            className="text-red-600 hover:!text-red-600 hover:!bg-red-100"
          >
            <ShieldOff className="mr-2 h-4 w-4" />
            Desativar usuário
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => onChangeStatus?.(user, 'APPROVED')}>
            <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
            Reativar usuário
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 pb-2 pt-1 text-xs text-muted-foreground">
          Status atual: <span className="font-medium">{statusMeta.label}</span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const createAdminUserColumns = ({
  onEdit,
  onChangeStatus,
  onResetPassword,
}: CreateColumnsOptions = {}): ColumnDef<AppUser>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Selecionar todos"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'user_metadata',
    header: 'Usuário',
    cell: ({ row }) => {
      const displayName = getUserDisplayName(row.original);
      const email = row.original.email || '';
      const avatarUrl = row.original.avatar_url || row.original.user_metadata?.avatar_url;

      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 border border-border/70 shadow-sm">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback>{getUserInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-foreground">{displayName}</span>
            <span className="truncate text-sm text-muted-foreground">{email}</span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as AppUser['status'];
      const meta = ADMIN_USER_STATUS_META[status];

      return (
        <Badge variant="outline" className={`font-medium ${meta.tone}`}>
          {meta.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'role',
    header: 'Papel',
    cell: ({ row }) => (
      <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
        {getRoleLabel(row.getValue('role') as string)}
      </Badge>
    ),
  },
  {
    accessorKey: 'last_sign_in_at',
    header: 'Último acesso',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium text-foreground">
          {row.original.last_sign_in_at
            ? formatUserDate(row.original.last_sign_in_at, { dateStyle: 'short' })
            : 'Sem acesso'}
        </span>
        <span className="text-xs text-muted-foreground">
          {row.original.last_sign_in_at
            ? formatUserDate(row.original.last_sign_in_at, { timeStyle: 'short' })
            : 'Conta nunca utilizada'}
        </span>
      </div>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Criação',
    cell: ({ row }) => formatUserDate(row.original.created_at, { dateStyle: 'short' }),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <UserActionsCell
        user={row.original}
        onEdit={onEdit}
        onChangeStatus={onChangeStatus}
        onResetPassword={onResetPassword}
      />
    ),
  },
];
